import { type ReactElement, type ReactNode } from 'react';
import { Route } from 'react-router-dom';
import type { ModuleUI } from './module';
import type { ScreenDef } from './screens/types';
import { ResourceListScreen } from './screens/ResourceListScreen';
import { ResourceDetailScreen } from './screens/ResourceDetailScreen';
import { ResourceFormScreen } from './screens/ResourceFormScreen';

// Turns modules[].screens into <Route> elements. Generic kinds render the engine screens;
// `custom` (or any kind with `component`) renders the given component. Mount the result
// inside a layout <Route element={<AppShell/>}> so every screen gets the shell.

function elementFor(def: ScreenDef): ReactElement {
  if (def.component) {
    const C = def.component;
    return <C />;
  }
  switch (def.kind) {
    case 'list':
    case 'board':
    case 'timeline':
    case 'calendar':
      return <ResourceListScreen def={def.kind === 'list' ? def : { ...def, views: def.views ?? [def.kind, 'table'] }} />;
    case 'detail':
      return <ResourceDetailScreen def={def} />;
    case 'form':
      return <FormPage def={def} />;
    default:
      return <div className="hf-page">Screen “{def.path}” has no component.</div>;
  }
}

function FormPage({ def }: { def: ScreenDef }) {
  return <ResourceFormScreen resource={def.resource} mode="page" form={def.form} onClose={() => window.history.back()} title={def.title} description={def.subtitle} />;
}

/** All screens across modules, in registration order. */
export function allScreens(modules: ModuleUI[]): ScreenDef[] {
  return modules.flatMap((m) => m.screens);
}

/** <Route>s for every module screen. Use inside <Routes>. */
export function moduleRoutes(modules: ModuleUI[]): ReactNode {
  return allScreens(modules).map((def) => <Route key={def.path} path={def.path} element={elementFor(def)} />);
}

/** Count of engine exceptions (custom screens) — surfaced for the governance budget. */
export function engineExceptions(modules: ModuleUI[]): ScreenDef[] {
  return allScreens(modules).filter((s) => s.kind === 'custom');
}
